<template>
  <!-- Offline-duration lock: the till stayed offline past `general.offlineMinutesLimit`
       (ConnectionService.lockApp). Non-dismissable; going back online lifts it. The invisible
       button in the box's bottom-right corner unlocks after 20 taps within the rolling window
       (support escape hatch). An inactive account shows its own lock instead, which has no such
       unlock. The box is the account lock's (AccountInactiveLock.vue), without buttons. -->
  <div
    v-if="store.lockedReason === 'offlineDuration' && !accountInactive"
    class="fixed inset-0 z-50 flex items-center justify-center bg-(--app-scrim)"
    data-testid="offline-duration-lock"
  >
    <div class="offline-lock">
      <div class="header text-panel-title">
        <UIcon class="size-5 shrink-0 text-error" name="i-lucide-circle-alert" />
        <span>{{ t('connection.internet_offline_too_long') }}</span>
      </div>
      <div class="message">{{ t('connection.internet_offline_too_long_message') }}</div>
      <button
        type="button"
        class="absolute right-0 bottom-0 w-15 cursor-default py-2 opacity-0"
        data-testid="offline-duration-unlock"
        @click="hiddenClick"
      >
        {{ t('general.no') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { TenantService, connectionService } from 'app-core'
import { useAppStore } from '@/stores/useAppStore'

const { t } = useI18n()
const store = useAppStore()

const accountInactive = computed(() => TenantService.isInactive(store.tenant))

let hiddenClickCount: number = 0
let hiddenClickTimeout: number | undefined = undefined

const hiddenClick = (): void => {
  hiddenClickCount++
  if(hiddenClickCount === 20) {
    connectionService.deleteOfflineFromTimestamp()
    connectionService.unlockApp()

    hiddenClickCount = 0
    clearTimeout(hiddenClickTimeout)
    return
  }

  clearTimeout(hiddenClickTimeout)
  hiddenClickTimeout = window.setTimeout(() => {
    hiddenClickCount = 0
  }, 2500)
}

onUnmounted(() => {
  if (hiddenClickTimeout) clearTimeout(hiddenClickTimeout)
})
</script>

<style lang="scss" scoped>
// The box of AccountInactiveLock.vue (the small-dialog box of CodOverlayAlert.vue) without its
// buttons row; `position: relative` anchors the hidden unlock in the bottom-right corner, out of
// the layout, so no empty strip is left under the message.
.offline-lock {
  position: relative;
  width: 400px;
  max-width: calc(100vw - 32px);
  background: var(--ui-bg);
  border-radius: var(--radius-frame);
  box-shadow: var(--app-shadow-modal);
  overflow: hidden;

  .header {
    padding: 16px 16px 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .message {
    padding: 12px 16px 16px;
  }
}
</style>
